import { sleep } from '../../base/performance/sleep';
import { onShow } from '../lifecycle/onShow';
import { onHide } from '../lifecycle/onHide';
import { clearHqRequest, removeHqHandler, sendHqRequest } from './bridge';
import { mapFields } from './fields';
import { buildHqRequestParams } from './request';
import type { AllStockHqInfo, CodeWithMarket, HqRequestParams, StockHqInfo } from './types';

/**
 * ### 获取股票列表行情数据
 *
 * 自动根据 App 版本选择协议：
 * - 期货通 iOS < 037.08.389：使用 `getHQ` 原生接口
 * - 其他平台/版本：使用 `UnifiedRequestBridge`
 *
 * @example
 * ```ts
 * import { HqManager } from '@fu/matrix';
 *
 * const hqManager = new HqManager({
 *   onlineId: 'myPage',
 *   callback: (res) => {
 *     console.log('行情更新:', res);
 *   },
 * });
 *
 * // 订阅股票（code_market 格式，与已有做并集）
 * hqManager.registerStocks(['300033_33', '000099_33']);
 * hqManager.registerStocks(['600519_11']); // 现在订阅了 3 只
 *
 * // 取消部分股票
 * hqManager.unregisterStocks(['000099_33']);  // 移除 000099，还剩 2 只
 *
 * // 清空全部
 * hqManager.clearStocks();
 *
 * // 页面销毁
 * hqManager.destroy();
 * ```
 *
 * 页面切后台自动暂停，回前台自动恢复，无需手动处理。
 */
export class HqManager {
  debug = false;

  invoked = false;

  connecting = false;

  connectDelay = 100;

  visible = true;

  private _destroyed = false;

  private _boundResume = this.resume.bind(this);

  private _boundPause = this.pause.bind(this);

  private connectVersion = 0;

  private _stockRefs = new Map<CodeWithMarket, number>();

  get codeWithMarkets(): CodeWithMarket[] {
    return Array.from(this._stockRefs.keys());
  }

  error: any = null;

  onlineId = 'demo';

  protocolId = 1264;

  pageId = 9001;

  columOrder = ['4', '34338', '10', '34818', '55'];

  logTableFields = ['10', '34818'];

  callback: (res: AllStockHqInfo) => void = () => {};

  constructor(config: {
    onlineId: string;
    debug?: boolean;
    connectDelay?: number;
    protocolId?: number;
    pageId?: number;
    columOrder?: string[];
    callback: (res: AllStockHqInfo) => void;
  }) {
    Object.keys(config).forEach((key) => {
      if (key in this) {
        // @ts-ignore
        this[key] = config[key];
      }
    });

    if (this.debug) {
      // @ts-ignore
      window.hqIns = this;
      // @ts-ignore
      window[`hqIns_${this.onlineId}`] = this;
    }

    onShow(this._boundResume);
    onHide(this._boundPause);
  }

  private get canConnect() {
    return this.visible && this._stockRefs.size > 0 && !this.connecting;
  }

  registerStocks(stocks: Array<CodeWithMarket>) {
    let changed = false;
    stocks.forEach((s) => {
      const count = this._stockRefs.get(s) || 0;
      this._stockRefs.set(s, count + 1);
      if (count === 0) changed = true;
    });
    if (!changed) return;

    this.connect();
  }

  unregisterStocks(stocks: Array<CodeWithMarket>) {
    let changed = false;
    stocks.forEach((s) => {
      const count = this._stockRefs.get(s);
      if (count === undefined) return;
      if (count <= 1) {
        this._stockRefs.delete(s);
      } else {
        this._stockRefs.set(s, count - 1);
      }
      changed = true;
    });
    if (!changed) return;

    this.connect();
  }

  clearStocks() {
    this.connectVersion += 1;
    this._stockRefs.clear();

    const reqParams = buildHqRequestParams({
      stocks: [],
      onlineId: this.onlineId,
      protocolId: this.protocolId,
      pageId: this.pageId,
      columOrder: this.columOrder,
    });
    this.sendRequest(reqParams);
  }

  async connect() {
    const version = ++this.connectVersion;
    this.disconnect();

    if (!this.canConnect) {
      return;
    }

    if (!this.invoked) {
      this.invoked = true;
    } else {
      this.connecting = true;
      await sleep(this.connectDelay);
      this.connecting = false;
    }

    if (version !== this.connectVersion || !this.canConnect) {
      return;
    }

    const reqParams = buildHqRequestParams({
      stocks: this.codeWithMarkets,
      onlineId: this.onlineId,
      protocolId: this.protocolId,
      pageId: this.pageId,
      columOrder: this.columOrder,
    });

    this.sendRequest(reqParams);

    this.log('connect', reqParams.requestDic);
  }

  private resume() {
    if (this._destroyed) return;
    this.visible = true;
    this.connect();
  }

  private pause() {
    if (this._destroyed) return;
    this.visible = false;
    this.connectVersion += 1;
    this.disconnect();
  }

  private sendRequest(reqParams: HqRequestParams) {
    sendHqRequest(reqParams, {
      onData: this.handleHqData.bind(this),
      onError: (error) => {
        this.error = error;
        this.log('请求或解析失败', error);
      },
    });
  }

  disconnect() {
    clearHqRequest(this.onlineId);
    this.log('hq disconnect');
  }

  destroy() {
    this._destroyed = true;

    // @ts-ignore
    if (window.hqIns === this) {
      // @ts-ignore
      window.hqIns = null;
    }
    // @ts-ignore
    window[`hqIns_${this.onlineId}`] = null;

    this._stockRefs.clear();
    this.disconnect();
    removeHqHandler(this.onlineId);
    this.log('hq destroy');
  }

  log(...args: any[]) {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log(`[HqManager:${this.onlineId}:${new Date().toLocaleTimeString()}]:`, ...args);
    }
  }

  private logTable(data: any, columns: any[]) {
    if (this.debug) {
      console.table(data, columns);
    }
  }

  handleHqData(rawData: Record<string, string[]>) {
    if (!rawData || Object.keys(rawData).length < 1) {
      return;
    }

    const data = mapFields(rawData);
    const stockCodes = data['code'] || [];
    const marketIds = data['market'] || [];
    const stockObj = {} as AllStockHqInfo;
    const colKeys = Object.keys(data);

    for (let i = 0; i < stockCodes.length; i++) {
      const stockKey = `${stockCodes[i]}_${marketIds[i]}` as CodeWithMarket;
      stockObj[stockKey] = {} as StockHqInfo;

      colKeys.forEach((colKey) => {
        stockObj[stockKey][colKey] = data[colKey]?.[i] ?? '';
      });
    }

    this.logTable(stockObj, this.logTableFields);
    this.callback(stockObj);
  }
}